import React, { useContext, useState, useMemo } from "react";
import "./ContentBilling.scss";
import { EventUpdatedContext } from "../../../store/EventUpdatedContext";
import { getIdWithToken } from "../../../utils/storage/Token";
import { useFetchDashHome, DashHomeData } from "../../../service/Business/DashHomeService";
import FCSpinner from "../../FCObjets/FCSpinner";
import StatusBilling, { TypeStatusBilling } from "../../../utils/Business/StatusBilling";
import { TypeForm } from "../../../types/EnumTypeForm";
import { BillingInterface } from "../../../service/Business/Interfaces/BillingInterface";
import { DeleteBilling, useFetchBilling } from "../../../service/Business/BillingService";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import { EnumTypeMail } from "../../../types/Business/EnumTypeMail";
import FCContentModalFL, { DataForm } from "../../FCObjets/Form/FCContentModalFL";
import FCTag from "../../FCObjets/FCTag";
import FCToggleSwitch from "../../FCObjets/FCToggleSwitch";
import FCSortableHeader from "../../FCObjets/FCSortableHeader";
import { useTableSort } from "../../../hook/useTableSort";
import formatEuro from "../../../utils/formatEuro";
import { useToast } from "../../FCObjets/FCToast/FCToast";

type BillingSortKey = "contract" | "number" | "state" | "totalHT" | "outlay" | "totalTTC" | "begin";

function ContentBilling() {
    const toast = useToast();

    let { cptUpdated, setUpdated } = useContext(EventUpdatedContext);
    const { data: billings, loading, error } = useFetchBilling(getIdWithToken(), cptUpdated);
    const { data: dashData } = useFetchDashHome(getIdWithToken());
    const estimationCotisations = (dashData as DashHomeData | null)?.estimationCotisations ?? null;
    const [showBillingPayed, setShowBillingPayed] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const [data, setData] = useState<DataForm>({
        type: TypeForm.EMPTY,
        data: undefined,
    });

    /**
     * Gestion de l'ouverture du modal
     */
    const gestionModal = (open: boolean, e: BillingInterface | any | undefined, type: TypeForm | undefined) => {
        let t = TypeForm.EMPTY;
        if (open && type) {
            t = type;
        }
        setData({ type: t, data: e });
    };

    /**
     * Calcule les totaux d'une facture
     */
    const getBillingTotals = (billing: BillingInterface) => {
        let totalHT = 0;
        let totalTTC = 0;
        if (billing.billingItems) {
            totalHT = billing.billingItems.reduce((acc, item) => acc + item.totalAmountExclTax, 0);
            totalTTC = billing.billingItems.reduce((acc, item) => acc + item.totalAmountInclTax, 0);
        }
        return { totalHT, totalTTC };
    };

    /**
     * Factures filtrées
     */
    const filteredBillings = useMemo(() => {
        if (!billings) return [];
        return billings.filter((e: BillingInterface) => {
            if (
                (e.state === TypeStatusBilling.BILL_PAID || e.state === TypeStatusBilling.CANCELED) &&
                !showBillingPayed
            ) {
                return false;
            }
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                return (
                    e.businessDeal.name.toLowerCase().includes(q) ||
                    e.number.toLowerCase().includes(q) ||
                    e.businessDeal.customer.companyName.toLowerCase().includes(q)
                );
            }
            return true;
        });
    }, [billings, showBillingPayed, searchQuery]);

    /**
     * Tri des factures — accesseurs par colonne
     */
    const {
        sortedData: sortedBillings,
        sortState,
        handleSort,
    } = useTableSort<BillingInterface, BillingSortKey>(filteredBillings, {
        contract: (b) => b.businessDeal.name,
        number: (b) => b.number,
        state: (b) => b.state,
        totalHT: (b) => getBillingTotals(b).totalHT,
        outlay: (b) => b.outlay,
        totalTTC: (b) => getBillingTotals(b).totalTTC,
        begin: (b) => new Date(b.begin),
    });

    /**
     * Statistiques KPI
     */
    const stats = useMemo(() => {
        if (!billings) return { totalHT: 0, enAttente: 0, payeCeMois: 0 };
        const all = billings as BillingInterface[];

        let totalHT = 0;
        let enAttente = 0;
        let payeCeMois = 0;
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        all.forEach((b: BillingInterface) => {
            const { totalHT: ht } = getBillingTotals(b);
            if (b.state !== TypeStatusBilling.CANCELED) {
                totalHT += ht;
            }
            if (b.state === TypeStatusBilling.BILL_SENT || b.state === TypeStatusBilling.BILL_INIT) {
                enAttente += ht;
            }
            if (b.state === TypeStatusBilling.BILL_PAID) {
                const endDate = new Date(b.end);
                if (endDate.getMonth() === currentMonth && endDate.getFullYear() === currentYear) {
                    payeCeMois += ht;
                }
            }
        });

        return { totalHT, enAttente, payeCeMois };
    }, [billings]);

    /**
     * Rendu des boutons d'action contextuels selon le statut
     */
    const renderActions = (billing: BillingInterface, isMobile: boolean) => {
        const btnClass = isMobile ? "billingMobileActionBtn" : "billingActionBtn";

        return (
            <div className={isMobile ? "billingMobileCardActions" : "billingCellActions"}>
                {/* Modifier - toujours visible */}
                <button
                    className={`${btnClass} billingActionEdit`}
                    onClick={() => gestionModal(true, billing, TypeForm.BILLING)}
                    title="Modifier"
                >
                    <span className="material-symbols-outlined">edit</span>
                    {isMobile && <span>Éditer</span>}
                </button>

                {/* BL PDF - états INITIALIZED ou DELIVERY_ORDER_INIT */}
                {(billing.state === TypeStatusBilling.INITIALIZED ||
                    billing.state === TypeStatusBilling.DELIVERY_ORDER_INIT) && (
                    <button
                        className={`${btnClass} billingActionPdf`}
                        onClick={() => {
                            window.open(`/pdf/bl/${billing.id}`, "_blank");
                            setTimeout(() => setUpdated(), 3000);
                        }}
                        title="Bon de livraison"
                    >
                        <span className="material-symbols-outlined">description</span>
                        {isMobile && <span>Aperçu</span>}
                    </button>
                )}

                {/* Envoyer BL - état DELIVERY_ORDER_INIT */}
                {billing.state === TypeStatusBilling.DELIVERY_ORDER_INIT && (
                    <button
                        className={`${btnClass} billingActionMail`}
                        onClick={() => {
                            billing.typeMail = EnumTypeMail.BL;
                            gestionModal(true, billing, TypeForm.SEND_MAIL);
                        }}
                        title="Envoyer bon de livraison"
                    >
                        <span className="material-symbols-outlined">send</span>
                        {isMobile && <span>Envoyer</span>}
                    </button>
                )}

                {/* Archiver BL - état DELIVERY_ORDER_SENT */}
                {billing.state === TypeStatusBilling.DELIVERY_ORDER_SENT && (
                    <button
                        className={`${btnClass} billingActionArchive`}
                        onClick={() => gestionModal(true, billing, TypeForm.UPLOAD_BL)}
                        title="Archiver bon de livraison"
                    >
                        <span className="material-symbols-outlined">inventory_2</span>
                    </button>
                )}

                {/* Archiver Facture - état DELIVERY_ORDER_SIGNED */}
                {billing.state === TypeStatusBilling.DELIVERY_ORDER_SIGNED && (
                    <button
                        className={`${btnClass} billingActionArchive`}
                        onClick={() => gestionModal(true, billing, TypeForm.UPLOAD_BILLING)}
                        title="Archiver facture"
                    >
                        <span className="material-symbols-outlined">inventory_2</span>
                    </button>
                )}

                {/* Envoyer Facture - état BILL_INIT */}
                {billing.state === TypeStatusBilling.BILL_INIT && (
                    <button
                        className={`${btnClass} billingActionMail`}
                        onClick={() => {
                            billing.typeMail = EnumTypeMail.BILLING;
                            gestionModal(true, billing, TypeForm.SEND_MAIL);
                        }}
                        title="Envoyer facture"
                    >
                        <span className="material-symbols-outlined">send</span>
                        {isMobile && <span>Envoyer</span>}
                    </button>
                )}

                {/* PDF facture - toujours */}
                <button
                    className={`${btnClass} billingActionPdf`}
                    onClick={() => window.open(`/pdf/bl/${billing.id}`, "_blank")}
                    title="Voir PDF"
                >
                    <span className="material-symbols-outlined">picture_as_pdf</span>
                    {isMobile && <span>PDF</span>}
                </button>

                {/* Supprimer */}
                <button
                    className={`${btnClass} billingActionDelete`}
                    onClick={() => {
                        const onConfirm = () => {
                            DeleteBilling(billing).then(() => {
                                toast.success("Facture supprimée.", "Facture");
                                setUpdated();
                            });
                        };
                        const payload = {
                            title: `Suppression de la facture ${billing.number}`,
                            msg: "Êtes-vous sûr de vouloir supprimer cette facture ?",
                            onConfirm: onConfirm,
                        };
                        gestionModal(true, payload, TypeForm.CONFIRM);
                    }}
                    title="Supprimer"
                >
                    <span className="material-symbols-outlined">delete</span>
                </button>
            </div>
        );
    };

    return (
        <FCContentModalFL
            form={data}
            onModalFormFinished={() => {
                gestionModal(false, undefined, undefined);
                setUpdated();
            }}
        >
            <div className="billingPage">
                {/* Header */}
                <div className="billingHeader">
                    <div className="billingHeaderLeft">
                        <h1 className="billingTitle">Liste des Factures</h1>
                        <p className="billingSubtitle">
                            Gérez vos revenus et le suivi de vos contrats en temps réel depuis votre atelier financier.
                        </p>
                    </div>
                    <div className="billingHeaderRight">
                        <div className="billingSearchBar">
                            <span className="material-symbols-outlined billingSearchIcon">search</span>
                            <input
                                type="text"
                                className="billingSearchInput"
                                placeholder="Rechercher une facture..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <button
                            className="billingBtnAdd"
                            onClick={() => gestionModal(true, undefined, TypeForm.BILLING)}
                        >
                            <span className="material-symbols-outlined">add</span>
                            Nouvelle Facture
                        </button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="billingKpiGrid">
                    <div className="billingKpiCard billingKpiRevenu">
                        <div className="billingKpiLabel">Total Revenu HT</div>
                        <div className="billingKpiValue">{formatEuro(stats.totalHT, 0)}</div>
                        <div className="billingKpiDetails">
                            <div className="billingKpiDetail">
                                <span className="billingKpiDetailLabel">En attente</span>
                                <span className="billingKpiDetailValue">{formatEuro(stats.enAttente, 0)}</span>
                            </div>
                            <div className="billingKpiDetail">
                                <span className="billingKpiDetailLabel">Payé ce mois</span>
                                <span className="billingKpiDetailValue">{formatEuro(stats.payeCeMois, 0)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="billingKpiCard">
                        <div className="billingKpiLabel">Prochaine Échéance</div>
                        <div className="billingKpiEcheanceTitle">
                            URSSAF T{Math.ceil((new Date().getMonth() + 1) / 3)} {new Date().getFullYear()}
                        </div>
                        <div className="billingKpiEcheanceLabel">Estimation cotisations</div>
                        <div className="billingKpiEcheanceValue">
                            {estimationCotisations !== null ? formatEuro(estimationCotisations, 0) : "---"}
                        </div>
                    </div>
                </div>

                {/* Search + toggle */}
                <div className="billingSectionHeader">
                    <div className="billingSectionActions">
                        <FCToggleSwitch
                            onToggle={(isChecked) => setShowBillingPayed(isChecked)}
                            defaultChecked={false}
                            label="Payées / Annulées"
                        />
                    </div>
                </div>

                {/* Loading */}
                {loading && (
                    <div className="billingLoading">
                        <FCSpinner size="large" />
                    </div>
                )}

                {/* Error */}
                {error && <FCAlertMsgErrorAPI error={error} />}

                {/* Desktop table */}
                {!loading && !error && (
                    <div className="billingTableWrapper">
                        <table className="billingTable">
                            <thead>
                                <tr>
                                    <th>
                                        <FCSortableHeader
                                            label="Nom Contrat"
                                            sortKey="contract"
                                            sortState={sortState}
                                            onSort={handleSort}
                                        />
                                    </th>
                                    <th>
                                        <FCSortableHeader
                                            label="Numéro"
                                            sortKey="number"
                                            sortState={sortState}
                                            onSort={handleSort}
                                        />
                                    </th>
                                    <th>
                                        <FCSortableHeader
                                            label="Statut"
                                            sortKey="state"
                                            sortState={sortState}
                                            onSort={handleSort}
                                        />
                                    </th>
                                    <th>
                                        <FCSortableHeader
                                            label="Tot HT"
                                            sortKey="totalHT"
                                            sortState={sortState}
                                            onSort={handleSort}
                                        />
                                    </th>
                                    <th>
                                        <FCSortableHeader
                                            label="Tot débours"
                                            sortKey="outlay"
                                            sortState={sortState}
                                            onSort={handleSort}
                                        />
                                    </th>
                                    <th>
                                        <FCSortableHeader
                                            label="Tot TTC"
                                            sortKey="totalTTC"
                                            sortState={sortState}
                                            onSort={handleSort}
                                        />
                                    </th>
                                    <th>
                                        <FCSortableHeader
                                            label="Dates"
                                            sortKey="begin"
                                            sortState={sortState}
                                            onSort={handleSort}
                                        />
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedBillings.length > 0 ? (
                                    sortedBillings.map((billing: BillingInterface, index: React.Key) => {
                                        const { totalHT, totalTTC } = getBillingTotals(billing);
                                        return (
                                            <tr key={index}>
                                                <td>
                                                    <div className="billingCellContract">
                                                        <div className="billingContractName">
                                                            {billing.businessDeal.name}
                                                        </div>
                                                        <div className="billingContractClient">
                                                            {billing.businessDeal.customer.companyName}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="billingCellNumber">#{billing.number}</span>
                                                </td>
                                                <td>
                                                    <FCTag
                                                        value={StatusBilling.toString(billing.state)}
                                                        color={StatusBilling.toColor(billing.state)}
                                                        style={{ fontWeight: "bold", fontSize: "11px" }}
                                                    />
                                                </td>
                                                <td>
                                                    <span className="billingCellAmount">{formatEuro(totalHT)}</span>
                                                </td>
                                                <td>
                                                    <span className="billingCellAmountMuted">
                                                        {formatEuro(billing.outlay)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="billingCellAmountBold">
                                                        {formatEuro(totalTTC)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="billingCellDates">
                                                        <span>
                                                            Du {new Date(billing.begin).toLocaleDateString("fr-FR")}
                                                        </span>
                                                        <span>
                                                            Au {new Date(billing.end).toLocaleDateString("fr-FR")}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>{renderActions(billing, false)}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={8}>
                                            <div className="billingEmptyState">Aucune facture trouvée</div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {sortedBillings.length > 0 && (
                            <div className="billingTableFooter">
                                <span className="billingTableCount">
                                    Affichage de {sortedBillings.length} sur {billings ? billings.length : 0} factures
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Mobile cards */}
                {!loading && !error && (
                    <div className="billingMobileList">
                        {sortedBillings.length > 0 ? (
                            sortedBillings.map((billing: BillingInterface, index: React.Key) => {
                                const { totalHT, totalTTC } = getBillingTotals(billing);
                                return (
                                    <div className="billingMobileCard" key={index}>
                                        <div className="billingMobileCardHeader">
                                            <FCTag
                                                value={StatusBilling.toString(billing.state)}
                                                color={StatusBilling.toColor(billing.state)}
                                                style={{ fontWeight: "bold", fontSize: "10px" }}
                                            />
                                        </div>
                                        <h3 className="billingMobileCardName">{billing.businessDeal.name}</h3>
                                        <div className="billingMobileCardMeta">
                                            Facture N° {billing.number} | {billing.businessDeal.customer.companyName}
                                        </div>
                                        <div className="billingMobileCardAmounts">
                                            <div className="billingMobileCardAmountCol">
                                                <span className="billingMobileCardAmountLabel">Total HT</span>
                                                <span className="billingMobileCardAmountValue">
                                                    {formatEuro(totalHT)}
                                                </span>
                                            </div>
                                            <div className="billingMobileCardAmountCol">
                                                <span className="billingMobileCardAmountLabel">Total TTC</span>
                                                <span className="billingMobileCardAmountValueGold">
                                                    {formatEuro(totalTTC)}
                                                </span>
                                            </div>
                                        </div>
                                        {renderActions(billing, true)}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="billingEmptyState">Aucune facture trouvée</div>
                        )}
                    </div>
                )}
            </div>
        </FCContentModalFL>
    );
}

export default ContentBilling;
